import { objectSpread2 as _objectSpread2, defineProperty as _defineProperty } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { resolveInputOutline } from '../../styles-system/input-element-utils.esm.js';
import { zIndices } from '../../themes/const.esm.js';
import { transitions } from '../../themes/theme-keys/transitions.esm.js';
import { getSafeReadableColor, getSafeTint } from '../../utils/colors.esm.js';
import { resolveColor } from '../../utils/styled-props.esm.js';

var _default, _zIndices$modal;
var baseControl = function baseControl(theme, props) {
  var state = props.state,
    isDisabled = props.isDisabled,
    isFocused = props.isFocused,
    menuIsOpen = props.menuIsOpen;
  return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
    bg: 'white'
  }, resolveInputOutline(theme, state ? state : 'muted')), isDisabled ? {
    backgroundColor: 'neutral.light'
  } : {}), isDisabled || isFocused || menuIsOpen ? {} : {
    '&:hover': resolveInputOutline(theme, state ? state : 'primary')
  }), isFocused || menuIsOpen ? _objectSpread2({}, resolveInputOutline(theme, state ? state : 'primary')) : {}), menuIsOpen ? _defineProperty({
    borderTopLeftRadius: 'default',
    borderTopRightRadius: 'default',
    borderBottom: 'unset',
    '&::before': {
      position: 'absolute',
      right: '1px',
      bottom: 0,
      left: '1px',
      bg: 'white',
      content: '""',
      height: '5px',
      zIndex: 104
    },
    '&::after': {
      position: 'absolute',
      right: 0,
      bottom: 0,
      left: 0,
      bg: 'neutral',
      content: '""',
      height: '1px',
      zIndex: 104
    }
  }, '& .csg-tui-select-value-container', {
    zIndex: 105
  }) : {
    borderRadius: 'default'
  });
};
var baseSingleValue = function baseSingleValue(_t, props) {
  return {
    color: props.isDisabled ? 'neutral.dark' : 'text',
    emMarginX: 'xs-2',
    maxWidth: 'calc(100% - 8px)',
    overflow: 'hidden',
    position: 'absolute',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };
};
var baseDropdownIndicator = function baseDropdownIndicator(_t, props) {
  return {
    color: props.isDisabled ? 'neutral.dark' : 'primary',
    display: 'flex',
    emPadding: 'xs-2',
    transition: transitions.create(['color', 'transform'], {
      duration: transitions.duration.shorter
    }),
    transform: "".concat(props.menuIsOpen ? 'rotate(180deg)' : 'rotate(0)'),
    '&:hover': {
      opacity: 0.6
    }
  };
};
var baseIndicator = function baseIndicator(_t, props) {
  return {
    color: props.isDisabled ? 'neutral.dark' : 'neutral.darker',
    display: 'flex',
    emPadding: 'xs-2'
  };
};
var baseMenu = function baseMenu(theme, props) {
  var _ref2;
  var state = props.state,
    placement = props.placement;
  return _ref2 = {
    variant: 'text.body',
    width: '100%',
    mt: '-1px'
  }, _defineProperty(_ref2, "".concat(placement === 'top' ? 'bottom' : 'top'), '100%'), _defineProperty(_ref2, "bg", 'white'), _defineProperty(_ref2, "zIndex", 102), _defineProperty(_ref2, "borderBottomLeftRadius", 'default'), _defineProperty(_ref2, "borderBottomRightRadius", 'default'), _defineProperty(_ref2, '&::before', _objectSpread2(_objectSpread2({
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    pointerEvents: 'none'
  }, resolveInputOutline(theme, state ? state : 'primary')), {}, {
    zIndex: 99
  })), _ref2;
};
var baseMultiValue = {
  bg: 'neutrals.20',
  borderRadius: 'pill',
  transition: transitions.create(),
  '> button': {
    transition: transitions.create('width')
  },
  '.csg-tui-icon': {
    transition: transitions.create('opacity')
  },
  '&.is-disabled': {
    bg: 'transparent',
    p: '0.25em 0',
    '> label': {
      m: 0,
      lineHeight: '1.2em'
    },
    '> button': {
      width: 0
    },
    '.csg-tui-icon': {
      opacity: 0
    }
  }
};
var baseOption = function baseOption(theme, props) {
  return _objectSpread2({
    color: 'text',
    paddingX: 'sm',
    emPaddingY: 'xs-2',
    WebkitTapHighlightColor: resolveColor(theme, 'secondary')
  }, !props.disabled ? _objectSpread2({}, props.selected ? {
    bg: 'primary',
    color: 'white',
    '&:active': {
      textShadow: 'textWhite'
    }
  } : {
    '&:hover': {
      color: 'primary',
      textShadow: 'textPrimary'
    }
  }) : {
    bg: 'neutral.subtler',
    color: 'neutral.dark',
    '&:hover': {
      cursor: 'not-allowed'
    }
  });
};
var baseSelect = {
  selectContainer: {
    variant: 'text.body',
    lineHeight: 1.5
  },
  valueContainer: {
    emPaddingY: 'xs-2',
    emPaddingX: 'xxs'
  },
  control: baseControl,
  singleValue: baseSingleValue,
  dropdownIndicator: baseDropdownIndicator,
  clearIndicator: function clearIndicator(theme, props) {
    return _objectSpread2(_objectSpread2({}, baseIndicator(theme, props)), {}, {
      opacity: 0.6
    });
  },
  loadingIndicator: {
    emPaddingY: '0.05em',
    emPaddingX: 'xxs',
    color: 'primary'
  },
  menuList: {
    variant: 'scrollbar.transition'
  },
  menu: baseMenu,
  option: baseOption,
  multiValue: baseMultiValue,
  placeholder: {
    color: 'neutral.dark'
  }
};

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @deprecated react-select is deprecated. please switch to AutoComplete
 */
var selectVariants = {
  select: baseSelect,
  dataDrivenSample: _objectSpread2(_objectSpread2({}, baseSelect), {}, {
    selectContainer: _objectSpread2(_objectSpread2({}, baseSelect.selectContainer), {}, {
      width: '300px'
    }),
    singleValue: function singleValue(theme, props) {
      var _props$data;
      return _objectSpread2(_objectSpread2({}, baseSingleValue(theme, props)), {}, {
        color: props.isDisabled ? 'neutrals.12' : ((_props$data = props.data) === null || _props$data === void 0 ? void 0 : _props$data.color) || 'text'
      });
    },
    menu: function menu(theme, props) {
      return _objectSpread2(_objectSpread2({}, baseMenu(theme, props)), {}, {
        width: '400px'
      });
    },
    option: function option(theme, props) {
      var _props$data2, _props$data3, _props$data4, _props$data5, _props$data6;
      return _objectSpread2(_objectSpread2({}, baseOption(theme, props)), !props.disabled ? {
        bg: props.selected ? (_props$data2 = props.data) === null || _props$data2 === void 0 ? void 0 : _props$data2.color : 'transparent',
        color: props.selected ? getSafeReadableColor((_props$data3 = props.data) === null || _props$data3 === void 0 ? void 0 : _props$data3.color) : (_props$data4 = props.data) === null || _props$data4 === void 0 ? void 0 : _props$data4.color,
        '&:hover': {
          bg: getSafeTint(0.7, (_props$data5 = props.data) === null || _props$data5 === void 0 ? void 0 : _props$data5.color),
          textShadow: "0 0 1px ".concat((_props$data6 = props.data) === null || _props$data6 === void 0 ? void 0 : _props$data6.color)
        }
      } : {});
    }
  }),
  demographicsRadiusSelector: _objectSpread2(_objectSpread2({}, baseSelect), {}, {
    selectContainer: _objectSpread2(_objectSpread2({}, baseSelect.selectContainer), {}, {
      width: '300px'
    })
  }),
  search: _objectSpread2(_objectSpread2({}, baseSelect), {}, {
    searchIndicator: baseIndicator
  }),
  modalMode: _objectSpread2(_objectSpread2({}, baseSelect), {}, {
    selectContainer: _objectSpread2(_objectSpread2({}, baseSelect.selectContainer), {}, {
      zIndex: ((_default = zIndices === null || zIndices === void 0 ? void 0 : (_zIndices$modal = zIndices.modal) === null || _zIndices$modal === void 0 ? void 0 : _zIndices$modal['__default']) !== null && _default !== void 0 ? _default : 0) + 50
    }),
    searchIndicator: baseIndicator
  }),
  lenderAdmin: _objectSpread2(_objectSpread2({}, baseSelect), {}, {
    selectContainer: _objectSpread2(_objectSpread2({}, baseSelect.selectContainer), {}, {
      height: '32px'
    })
  }),
  ghost: _objectSpread2(_objectSpread2({}, baseSelect), {}, {
    control: undefined,
    singleValue: _objectSpread2(_objectSpread2({}, baseSelect.singleValue), {}, {
      position: 'inherit',
      color: 'inherit'
    })
  }),
  newsFilter: _objectSpread2(_objectSpread2({}, baseSelect), {}, {
    selectContainer: undefined,
    valueContainer: {
      px: 'sm',
      py: '2px',
      minHeight: '28px'
    },
    indicators: {
      minHeight: '28px'
    },
    menu: function menu(theme, props) {
      var _ref3;
      var state = props.state,
        placement = props.placement;
      return _ref3 = {
        fontFamily: 'costar',
        width: '100%',
        mt: '-1px'
      }, _defineProperty(_ref3, "".concat(placement === 'top' ? 'bottom' : 'top'), '100%'), _defineProperty(_ref3, "bg", 'white'), _defineProperty(_ref3, "zIndex", 102), _defineProperty(_ref3, "borderBottomLeftRadius", 'default'), _defineProperty(_ref3, "borderBottomRightRadius", 'default'), _defineProperty(_ref3, '&::before', _objectSpread2(_objectSpread2({
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        left: 0,
        bottom: 0,
        pointerEvents: 'none'
      }, resolveInputOutline(theme, state ? state : 'primary')), {}, {
        zIndex: 99
      })), _ref3;
    },
    option: function option(theme, props) {
      return _objectSpread2({
        color: 'text',
        bg: 'white',
        height: '32px',
        paddingX: 'sm',
        emPaddingY: 'xs',
        WebkitTapHighlightColor: resolveColor(theme, 'secondary')
      }, !props.disabled ? _objectSpread2({}, props.selected ? {
        bg: 'neutral.light',
        color: 'text',
        '&:active': {
          textShadow: 'textWhite'
        }
      } : {
        '&:hover': {
          color: 'white',
          bg: 'primary'
        }
      }) : {
        bg: 'neutral.lighter',
        color: 'neutral',
        '&:hover': {
          cursor: 'not-allowed'
        }
      });
    },
    singleValue: function singleValue(_t, props) {
      return {
        color: props.isDisabled ? 'neutral.dark' : 'text',
        maxWidth: 'calc(100% - 8px)',
        overflow: 'hidden',
        position: 'absolute',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      };
    },
    placeholder: {
      color: 'hsl(0, 0%, 50%)'
    }
  })
};

export default selectVariants;
//# sourceMappingURL=variants.esm.js.map
